import { useState, useEffect } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import TopBar from "../../components/layout/TopBar";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { cheersApi } from "../../api/cheers";
import { CHEER_EVENTS } from "../../store/cheerStore";
import useCheerStore from "../../store/cheerStore";

const EVENT_FILTER_ALL = "__all__";

export default function AdminCheersPage() {
  const { enabled, setEnabled } = useCheerStore();
  const [cheers,      setCheers]      = useState([]);
  const [text,        setText]        = useState("");
  const [eventType,   setEventType]   = useState("release");
  const [filterEvent, setFilterEvent] = useState(EVENT_FILTER_ALL);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);

  useEffect(() => {
    const ev = filterEvent === EVENT_FILTER_ALL ? undefined : filterEvent;
    cheersApi.list(ev).then(({ data }) => setCheers(data)).catch(() => {});
  }, [filterEvent]);

  const handleAdd = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await cheersApi.create(eventType, text.trim());
      setCheers(prev => [...prev, data]);
      setText("");
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await cheersApi.delete(id);
      setCheers(prev => prev.filter(c => c.id !== id));
    } catch {
      setError("Delete failed");
    }
  };

  const eventLabel = (key) => CHEER_EVENTS.find(e => e.key === key)?.label ?? key;

  const SELECT_CLS = "border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bh-500 bg-white";

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Cheers" subtitle="Encouraging messages shown after key events" actions={
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className="text-sm text-gray-600">{enabled ? "On" : "Off"}</span>
          <button
            role="switch"
            aria-checked={enabled}
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none
              ${enabled ? "bg-bh-600" : "bg-gray-300"}`}
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform
              ${enabled ? "translate-x-4" : "translate-x-1"}`} />
          </button>
        </label>
      } />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        <Card>
          <CardHeader>Add a cheer</CardHeader>
          <CardBody>
            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Event</label>
              <select value={eventType} onChange={e => setEventType(e.target.value)} className={SELECT_CLS}>
                {CHEER_EVENTS.map(ev => (
                  <option key={ev.key} value={ev.key}>{ev.emoji} {ev.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <textarea
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-bh-500"
                rows={2}
                placeholder="e.g. Outstanding work! That blade is going to sing. 🎵"
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAdd(); }}
              />
              <Button onClick={handleAdd} disabled={saving || !text.trim()} className="self-end shrink-0">
                <PlusIcon className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <span>{cheers.length} cheer{cheers.length !== 1 ? "s" : ""}</span>
              <select value={filterEvent} onChange={e => setFilterEvent(e.target.value)}
                className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-bh-500">
                <option value={EVENT_FILTER_ALL}>All events</option>
                {CHEER_EVENTS.map(ev => (
                  <option key={ev.key} value={ev.key}>{ev.emoji} {ev.label}</option>
                ))}
              </select>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {cheers.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-400 text-center">No cheers yet — add one above.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {cheers.map(c => (
                  <li key={c.id} className="flex items-start gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <span className="inline-block text-xs font-medium text-gray-400 mb-1">
                        {eventLabel(c.event_type)}
                      </span>
                      <p className="text-sm text-gray-700 leading-relaxed">{c.text}</p>
                    </div>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="shrink-0 text-gray-300 hover:text-red-500 transition-colors mt-0.5"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

      </div>
    </div>
  );
}
